import React, { useEffect, useState } from 'react';
import { Alert, Input, Modal, Radio, Space, Typography } from 'antd';
import { smartBetaApi, SmartBetaView } from '../../../services/smartBetaApi';
import { useIsMobile } from '../../../hooks/useIsMobile';

const { Text } = Typography;

interface Props {
  open: boolean;
  config: SmartBetaView;
  onCancel: () => void;
  onDone: (view: SmartBetaView | null, selling: boolean) => void;
}

/** End the smart beta: hand the shares over unsold, or sell everything first. */
const SmartBetaEndModal: React.FC<Props> = ({ open, config, onCancel, onDone }) => {
  const isMobile = useIsMobile();
  const [sell, setSell] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSell(false);
    setTyped('');
    setError(null);
  }, [open]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const out = await smartBetaApi.end(config.dataConfigId, sell);
      onDone(out.view, !!out.selling);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message ?? err.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="End this smart beta"
      open={open}
      onCancel={onCancel}
      onOk={submit}
      okText={sell ? 'Sell everything and end' : 'End now'}
      okButtonProps={{ danger: true, loading: busy, disabled: typed !== 'END' }}
      width={isMobile ? '100%' : 520}
      style={isMobile ? { top: 'auto', bottom: 0, margin: 0, maxWidth: '100vw', paddingBottom: 0 } : undefined}
      destroyOnClose
    >
      <Radio.Group value={sell} onChange={(e) => setSell(e.target.value)} style={{ width: '100%' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Radio value={false}>
            <Text strong>Keep the shares</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Nothing is sold. The shares move to Not trading on your Portfolio page and the cash is free for anything
              else. You can import them again later.
            </Text>
          </Radio>
          <Radio value>
            <Text strong>Sell everything first</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              The bot starts now and sells every ticker. The smart beta ends once it stops (by 14:25 at the latest);
              lots it could not sell move to Not trading. Only during the session.
            </Text>
          </Radio>
        </Space>
      </Radio.Group>
      <div style={{ marginTop: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Your schedule stays saved, so you can set the smart beta up again. Type <Text code>END</Text> to confirm.
        </Text>
        <Input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="END"
          aria-label="Type END"
          style={{ marginTop: 6 }}
        />
      </div>
      {error && <Alert type="error" showIcon message={error} style={{ marginTop: 12 }} />}
    </Modal>
  );
};

export default SmartBetaEndModal;
